import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff } from 'lucide-react';

/**
 * Talk-or-type text input with microphone control.
 * Uses Web Speech API for live transcript. Audio is never stored.
 */
export default function VoiceTextInput({ value, onChange, placeholder, multiline, className, ...props }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const toggleMic = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { return; } // Browser doesn't support
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-NZ';
    const baseText = value || '';
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onChange(baseText + (baseText ? ' ' : '') + transcript);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, [listening, value, onChange]);

  const hasSpeechAPI = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const InputTag = multiline ? 'textarea' : 'input';

  return (
    <div className="vti-wrap" data-testid={props['data-testid'] ? `${props['data-testid']}-vti` : undefined}>
      <InputTag
        type={multiline ? undefined : 'text'}
        className={`vti-input ${className || ''}`}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Talk or type...'}
        rows={multiline ? 3 : undefined}
        {...props}
      />
      {hasSpeechAPI && (
        <button type="button" className={`vti-mic ${listening ? 'active' : ''}`}
          onClick={toggleMic} title={listening ? 'Stop recording' : 'Speak'}
          data-testid={props['data-testid'] ? `${props['data-testid']}-mic` : 'voice-mic'}>
          {listening ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
      )}
    </div>
  );
}
